<template>
  <v-card>
    <v-toolbar dark color="secondary">
      Manage Block
    </v-toolbar>
    <v-card-text>
      <v-row align="center" justify="center">
        <v-col cols="12" md="9" class="pa-0 mt-6">
          <v-autocomplete v-model="form.blockId" solo dense clearable :items="blockOptions" label="Select a block" />
        </v-col>
        <v-col v-if="!form.isSelfPaced" cols="12" md="9" class="pa-0 mt-3">
          <v-menu
            v-model="menu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="form.programStartDate"
                label="Block start date"
                prepend-icon="mdi-calendar"
                readonly
                dense
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker v-model="form.programStartDate" @input="menu = false" :allowed-dates="allowedDates" />
          </v-menu>
        </v-col>
        <v-col cols="12" md="9" class="pa-0 mt-3">
          <v-checkbox v-model="form.isSelfPaced" label="Self Paced" />
        </v-col>
      </v-row>

      <v-row v-if="!form.isSelfPaced">
        <v-col cols="6"><v-btn @click="updateStartDate('restart')">Restart previous week</v-btn></v-col>
        <v-col cols="6"><v-btn @click="updateStartDate('forward')">Forward one week</v-btn></v-col>
      </v-row>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        v-if="mode == 'CREATE'"
        :disabled="form.isSelfPaced ? !form.blockId : !(form.programStartDate && form.blockId)"
        @click="createBlock"
        color="primary"
        outlined
        :loading="saving"
      >
        Create
      </v-btn>
      <v-btn
        v-else
        :disabled="!(form.programStartDate && form.blockId)"
        color="primary"
        @click="editBlock"
        outlined
        :loading="saving"
      >
        Update block
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {
  WORKOUT_BLOCKS_QUERY,
  COACHING_BLOCK_MUTATION,
  SUBSCRIPTION_BLOCK_QUERY,
} from "@/constants/graphql/blocks/workoutblocks";

export default {
  name: "ManageBlock",
  props: {
    userId: {
      type: String,
      required: true,
    },
    subscriptionBlockId: {
      type: String,
      required: false,
    },
    mode: {
      type: String,
      default: "CREATE",
    },
    selectedBlockId: {
      type: [Number, String],
      required: false,
    },
  },
  data() {
    return {
      blockOptions: [],
      menu: false,
      saving: false,
      form: {
        programStartDate: null,
        blockId: null,
        isSelfPaced: false,
      },
      allowedDates: (val) => new Date(val).getUTCDay() === 1,
    };
  },
  watch: {
    selectedBlockId: function(val) {
      this.form.blockId = val;
    },
  },
  methods: {
    updateStartDate(type) {
      if (type == "restart") {
        this.form.programStartDate = this.$moment(this.form.programStartDate)
          .add(1, "week")
          .format("YYYY-MM-DD");
      } else {
        this.form.programStartDate = this.$moment(this.form.programStartDate)
          .subtract(1, "week")
          .format("YYYY-MM-DD");
      }
    },
    getId(base64string) {
      let decoded = atob(base64string).split(":");
      if (decoded[1]) return decoded[1];
      return "";
    },
    createBlock() {
      this.saving = true;
      let input = {
        userId: this.userId,
        startDate: this.form.programStartDate ? this.form.programStartDate : this.$moment().format("YYYY-MM-DD"),
        blockId: this.form.blockId,
        isSelfPaced: this.form.isSelfPaced,
      };
      this.$apollo
        .mutate({
          mutation: COACHING_BLOCK_MUTATION,
          variables: {
            input: input,
          },
          refetchQueries: ["subscriptionActivityQuery"],
        })
        .then((result) => {
          this.saving = false;
          if (result.data.coachingSubscriptionBlockMutation.success) {
            this.$toasted.success("Block created", {
              icon: "arm-flex",
              duration: 2000,
              position: "bottom-center",
            });
          } else {
            this.$toasted.error("Something went wrong", {
              icon: "arm-flex",
              duration: 2000,
              position: "bottom-center",
            });
          }
        });
    },
    editBlock() {
      this.saving = true;
      let input = {
        subscriptionBlockId: this.getId(this.subscriptionBlockId),
        blockId: this.form.blockId,
        startDate: this.form.programStartDate,
        isSelfPaced: this.form.isSelfPaced,
      };
      this.$apollo
        .mutate({
          mutation: COACHING_BLOCK_MUTATION,
          variables: {
            input: input,
          },
          refetchQueries: ["subscriptionActivityQuery"],
        })
        .then((result) => {
          this.saving = false;
          if (result.data.coachingSubscriptionBlockMutation.success) {
            this.$toasted.success("Block updated", {
              icon: "arm-flex",
              duration: 2000,
              position: "bottom-center",
            });
          } else {
            this.$toasted.error("Something went wrong", {
              icon: "arm-flex",
              duration: 2000,
              position: "bottom-center",
            });
          }
        });
    },
  },
  apollo: {
    workoutBlocks: {
      query: WORKOUT_BLOCKS_QUERY,
      variables() {
        return {
          orderBy: "-createdAt",
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.blockOptions = [];
          response.data.workoutBlocks.edges.forEach((item) => {
            let extra = "";

            if (!item.node.visible) {
              extra = " (hidden)";
            } else if (item.node.productVariant) {
              extra = " (paid)";
            }

            this.blockOptions.push({
              text: `${item.node.title} ${extra}`,
              value: this.getId(item.node.id),
            });
          });
        }
      },
    },
    subscriptionBlock: {
      query: SUBSCRIPTION_BLOCK_QUERY,
      fetchPolicy: "cache-and-network",
      variables() {
        return {
          subscriptionBlockId: this.getId(this.subscriptionBlockId),
        };
      },
      skip() {
        return !this.subscriptionBlockId;
      },
      result(response) {
        if (response.data) {
          this.form.blockId = this.getId(response.data.subscriptionBlock.block.id);
          this.form.isSelfPaced = response.data.subscriptionBlock.isSelfPaced;
          this.form.programStartDate = response.data.subscriptionBlock.startDate;
        }
      },
    },
  },
};
</script>
<style scoped>
.v-menu__content {
  min-width: auto !important;
}
</style>
